/**
 * Bone-Based Face Customization
 *
 * Provides face shape customization by manipulating skeleton bone transforms.
 * This is a prototype/fallback approach - shape blendshapes are preferred.
 *
 * See: docs/FACE_CUSTOMIZATION_PLAN.md
 */

import * as THREE from 'three';

/**
 * Face customization configuration
 * All values range from -1 to 1, with 0 being the default
 */
export interface FaceCustomizationConfig {
  // Face Shape (bone-based - working)
  faceWidth: number;
  faceHeight: number;

  // Jaw (bone-based - working)
  jawWidth: number;
  chinLength: number;

  // Cheekbones (needs shape blendshapes - placeholder)
  cheekboneHeight: number;
  cheekFullness: number;

  // Nose (needs shape blendshapes - placeholder)
  noseWidth: number;
  noseLength: number;

  // Eyes (partially bone-based)
  eyeSize: number;
  eyeSpacing: number;

  // Lips (needs shape blendshapes - placeholder)
  lipFullness: number;
  lipWidth: number;
}

/**
 * Default face configuration (all zeros = default appearance)
 */
export const DEFAULT_FACE_CONFIG: FaceCustomizationConfig = {
  faceWidth: 0,
  faceHeight: 0,
  jawWidth: 0,
  chinLength: 0,
  cheekboneHeight: 0,
  cheekFullness: 0,
  noseWidth: 0,
  noseLength: 0,
  eyeSize: 0,
  eyeSpacing: 0,
  lipFullness: 0,
  lipWidth: 0,
};

/**
 * Bone names to look for in the skeleton
 * Updated for Human Generator model bone naming
 */
const BONE_NAMES = {
  head: ['head', 'Head', 'mixamorigHead', 'HG_Head'],
  jaw: ['jaw', 'Jaw', 'mixamorigJaw', 'HG_Jaw', 'LowerJaw', 'jaw_open_lt_rt_frwd'],
  jawUpper: ['jaw_upper', 'JawUpper'],
  leftEye: ['eyeballL', 'LeftEye', 'leftEye', 'Eye_L', 'Eye.L', 'mixamorigLeftEye', 'HG_Eye_L'],
  rightEye: ['eyeballR', 'RightEye', 'rightEye', 'Eye_R', 'Eye.R', 'mixamorigRightEye', 'HG_Eye_R'],
  eyeScale: ['eyes_scale'], // Human Generator uses this for eye sizing
  nose: ['Nose', 'nose', 'NoseBone'],
  neck: ['neck', 'Neck', 'mixamorigNeck', 'HG_Neck'],
};

/**
 * Find a bone by trying multiple naming conventions
 */
function findBone(skeleton: THREE.Skeleton, names: string[]): THREE.Bone | null {
  for (const name of names) {
    const bone = skeleton.bones.find(b => b.name === name || b.name.includes(name));
    if (bone) return bone;
  }
  return null;
}

/**
 * Store original bone transforms for reset
 */
interface BoneTransformCache {
  position: THREE.Vector3;
  scale: THREE.Vector3;
}

const originalTransforms = new Map<string, BoneTransformCache>();

/**
 * Cache original bone transforms (call once when model loads)
 */
export function cacheOriginalTransforms(skeleton: THREE.Skeleton): void {
  for (const bone of skeleton.bones) {
    if (!originalTransforms.has(bone.name)) {
      originalTransforms.set(bone.name, {
        position: bone.position.clone(),
        scale: bone.scale.clone(),
      });
    }
  }
  console.debug('🦴 Cached bone transforms:', originalTransforms.size);
}

/**
 * Apply face customization via bone scaling
 *
 * @param skeleton - The avatar skeleton
 * @param config - Face customization configuration
 */
export function applyBoneFaceCustomization(
  skeleton: THREE.Skeleton,
  config: Partial<FaceCustomizationConfig>
): void {
  // Merge with defaults
  const fullConfig = { ...DEFAULT_FACE_CONFIG, ...config };

  // Find bones
  const headBone = findBone(skeleton, BONE_NAMES.head);
  const jawBone = findBone(skeleton, BONE_NAMES.jaw);
  const jawUpperBone = findBone(skeleton, BONE_NAMES.jawUpper);
  const leftEyeBone = findBone(skeleton, BONE_NAMES.leftEye);
  const rightEyeBone = findBone(skeleton, BONE_NAMES.rightEye);
  const eyeScaleBone = findBone(skeleton, BONE_NAMES.eyeScale);

  // Log available bones for debugging
  console.log('🦴 Face customization bones:', {
    head: headBone?.name || 'not found',
    jaw: jawBone?.name || 'not found',
    jawUpper: jawUpperBone?.name || 'not found',
    leftEye: leftEyeBone?.name || 'not found',
    rightEye: rightEyeBone?.name || 'not found',
    eyeScale: eyeScaleBone?.name || 'not found',
  });

  // Face Width & Height (Head bone scale)
  // Scale range: 0.9 to 1.1 (subtle adjustments to avoid distortion)
  if (headBone) {
    const original = originalTransforms.get(headBone.name);
    const baseScaleX = original?.scale.x || 1;
    const baseScaleY = original?.scale.y || 1;

    // Width: scale on X axis
    headBone.scale.x = baseScaleX * (1 + fullConfig.faceWidth * 0.1);

    // Height: scale on Y axis
    headBone.scale.y = baseScaleY * (1 + fullConfig.faceHeight * 0.1);

    console.log('👤 Head scale:', headBone.scale.x.toFixed(2), headBone.scale.y.toFixed(2));
  }

  // NOTE: Jaw and Eye controls need shape keys, not bone manipulation
  // Bone-based jaw scaling affects animation bones, not face shape
  // Bone-based eye spacing moves eyeballs in sockets, looks wrong
  //
  // These are disabled - add shape keys in Blender instead:
  // - jawWidth, chinLength
  // - eyeSize, eyeSpacing
  // - noseWidth, noseLength
  // - cheekboneHeight, cheekFullness
  // - lipFullness, lipWidth

  // Update skeleton
  skeleton.update();

  console.log('✅ Face customization applied via bone scaling');
}

/**
 * Reset all bone transforms to original values
 */
export function resetBoneTransforms(skeleton: THREE.Skeleton): void {
  for (const bone of skeleton.bones) {
    const original = originalTransforms.get(bone.name);
    if (original) {
      bone.position.copy(original.position);
      bone.scale.copy(original.scale);
    }
  }
  skeleton.update();
  console.log('🔄 Bone transforms reset to original values');
}

/**
 * Log available bones in skeleton (for debugging)
 */
export function logSkeletonBones(skeleton: THREE.Skeleton): void {
  console.log('🦴 Available bones in skeleton:');
  for (const bone of skeleton.bones) {
    console.log(`  - ${bone.name}`);
  }
}
